import { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Search, Trash2, Loader2, MoreVertical, Edit2, Building2, Shield } from 'lucide-react';
import { SuperAdminLayout } from '@/components/super-admin/SuperAdminLayout';
import { ModuleFormDrawer } from '@/components/admin/ModuleFormDrawer';
import { saApi } from '@/contexts/SuperAdminAuthContext';
import { toast } from '@/components/ui/toast';
import { ConfirmModal } from '@/components/ConfirmModal';
import { SortTh, TableHeadRow } from '@/components/ui/sortable-table';

type ScopeTab = 'tenant' | 'platform';

interface AppModule {
  id: string; name: string; pageLink: string; icon: string;
  type: string; scope?: string; parentId: string | null; orderNumber: number;
  isActive: boolean;
  parent: { id: string; name: string } | null;
  children: { id: string; name: string; isActive: boolean }[];
}

const TAB_CONFIG: Record<ScopeTab, { label: string; icon: typeof Building2; description: string }> = {
  tenant:   { label: 'Vendor Menu',     icon: Building2, description: 'Modules that drive the tenant sidebar and Roles permission grid.' },
  platform: { label: 'Super-admin Menu', icon: Shield,    description: 'Modules used by the BrixLead platform team UI.' },
};

function ActionMenu({ mod, onEdit, onDelete }: {
  mod: AppModule; onEdit: () => void; onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const ref = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleToggle = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, left: rect.right - 144 });
    }
    setOpen(v => !v);
  };

  return (
    <div ref={ref}>
      <button
        ref={btnRef}
        onClick={handleToggle}
        className="p-1.5 rounded-md hover:bg-gray-100 text-[#94A3B8] hover:text-[#64748B] transition-colors"
      >
        <MoreVertical className="h-4 w-4" />
      </button>
      {open && (
        <div
          className="fixed z-50 w-36 rounded-lg border border-gray-100 bg-white shadow-lg py-1"
          style={{ top: menuPos.top, left: menuPos.left }}
        >
          <button
            onClick={() => { setOpen(false); onEdit(); }}
            className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-[#1E293B] hover:bg-gray-50 transition-colors"
          >
            <Edit2 className="h-3.5 w-3.5 text-[#6366F1]" /> Edit
          </button>
          <button
            onClick={() => { setOpen(false); onDelete(); }}
            className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
          >
            <Trash2 className="h-3.5 w-3.5" /> Delete
          </button>
        </div>
      )}
    </div>
  );
}

export function SuperAdminModulesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialScope: ScopeTab = searchParams.get('scope') === 'platform' ? 'platform' : 'tenant';

  const [activeScope, setActiveScope] = useState<ScopeTab>(initialScope);
  const [modules, setModules] = useState<AppModule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDrawer, setShowDrawer] = useState(false);
  const [editModule, setEditModule] = useState<AppModule | null>(null);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [confirmMod, setConfirmMod] = useState<AppModule | null>(null);
  const [confirmModLoading, setConfirmModLoading] = useState(false);
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [confirmBulkLoading, setConfirmBulkLoading] = useState(false);

  // Keep ?scope in sync with the active tab so refresh / share preserves it.
  useEffect(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('scope', activeScope);
      return next;
    }, { replace: true });
  }, [activeScope, setSearchParams]);

  const fetchModules = useCallback(async () => {
    setIsLoading(true);
    try {
      // Backend returns all scopes; we filter client-side so switching
      // tabs is instant after the first load (no extra round-trip).
      const { data } = await saApi.get('/admin/modules');
      setModules(data);
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to load modules');
    } finally { setIsLoading(false); }
  }, []);

  useEffect(() => { fetchModules(); }, []);

  // Clear selection when the user switches scope tabs — selected ids
  // belong to the previous scope and would be confusing carryover.
  useEffect(() => { setSelected(new Set()); }, [activeScope]);

  // Hierarchical sort: each main module is followed by its own children
  // (sorted by orderNumber) so the listing reads like a tree. Orphan
  // sub-modules (parent not in the current view, e.g. due to search)
  // drop to the bottom rather than disappear.
  const sortHierarchical = (mods: AppModule[]): AppModule[] => {
    const mains = mods
      .filter(m => m.type === 'M')
      .sort((a, b) => a.orderNumber - b.orderNumber);

    const subsByParent = new Map<string, AppModule[]>();
    for (const m of mods.filter(m => m.type === 'S')) {
      if (!m.parentId) continue;
      const arr = subsByParent.get(m.parentId) ?? [];
      arr.push(m);
      subsByParent.set(m.parentId, arr);
    }
    for (const arr of subsByParent.values()) {
      arr.sort((a, b) => a.orderNumber - b.orderNumber);
    }

    const result: AppModule[] = [];
    for (const main of mains) {
      result.push(main);
      const subs = subsByParent.get(main.id) ?? [];
      result.push(...subs);
      subsByParent.delete(main.id);
    }
    // Orphans — parent isn't in this filtered view
    for (const arr of subsByParent.values()) result.push(...arr);
    return result;
  };

  const filtered = sortHierarchical(
    modules
      // Defensive — `scope` is recent; rows without it default to "tenant".
      .filter(m => (m.scope ?? 'tenant') === activeScope)
      .filter(m =>
        !search || m.name.toLowerCase().includes(search.toLowerCase())
      ),
  );

  const allChecked = filtered.length > 0 && filtered.every(m => selected.has(m.id));
  const someChecked = filtered.some(m => selected.has(m.id));

  const toggleAll = () => {
    if (allChecked) {
      setSelected(prev => { const s = new Set(prev); filtered.forEach(m => s.delete(m.id)); return s; });
    } else {
      setSelected(prev => { const s = new Set(prev); filtered.forEach(m => s.add(m.id)); return s; });
    }
  };

  const toggleOne = (id: string) => {
    setSelected(prev => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const handleDelete = (mod: AppModule) => setConfirmMod(mod);

  const handleConfirmDelete = async () => {
    if (!confirmMod) return;
    setConfirmModLoading(true);
    setDeletingIds(prev => new Set(prev).add(confirmMod.id));
    try {
      await saApi.delete(`/admin/modules/${confirmMod.id}`);
      setSelected(prev => { const s = new Set(prev); s.delete(confirmMod.id); return s; });
      setConfirmMod(null);
      fetchModules();
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to delete module');
    } finally {
      setDeletingIds(prev => { const s = new Set(prev); s.delete(confirmMod.id); return s; });
      setConfirmModLoading(false);
    }
  };

  const handleBulkDelete = () => {
    const ids = [...selected].filter(id => filtered.some(m => m.id === id));
    if (!ids.length) return;
    setConfirmBulk(true);
  };

  const handleConfirmBulkDelete = async () => {
    const ids = [...selected].filter(id => filtered.some(m => m.id === id));
    setConfirmBulkLoading(true);
    setBulkDeleting(true);
    try {
      await Promise.all(ids.map(id => saApi.delete(`/admin/modules/${id}`)));
      setSelected(new Set());
      setConfirmBulk(false);
      fetchModules();
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to delete some modules');
    } finally { setBulkDeleting(false); setConfirmBulkLoading(false); }
  };

  const openEdit = (mod: AppModule) => { setEditModule(mod); setShowDrawer(true); };
  const openAdd  = () => { setEditModule(null); setShowDrawer(true); };

  const selectedInView = filtered.filter(m => selected.has(m.id)).length;

  return (
    <SuperAdminLayout title="Modules">
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-[#1E293B]">Modules</h2>
            <p className="text-sm text-[#64748B] mt-0.5">{TAB_CONFIG[activeScope].description}</p>
          </div>
          <button
            onClick={openAdd}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold transition-colors"
          >
            <Plus className="h-4 w-4" /> Add Module
          </button>
        </div>

        {/* Scope tabs — segmented control */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-1 flex max-w-xl">
          {(['tenant', 'platform'] as ScopeTab[]).map((s) => {
            const c = TAB_CONFIG[s];
            const Icon = c.icon;
            const active = activeScope === s;
            return (
              <button
                key={s}
                onClick={() => setActiveScope(s)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  active ? 'bg-[#6366F1] text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Icon className="h-4 w-4" />
                {c.label}
              </button>
            );
          })}
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search modules..."
              className="w-full h-10 pl-9 pr-4 rounded-lg text-sm border border-gray-200 bg-white text-[#1E293B] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#6366F1]/30 focus:border-[#6366F1] transition-all"
            />
          </div>

          <div className="flex items-center gap-2 ml-auto">
            {selectedInView > 0 && (
              <span className="text-sm text-[#64748B]">{selectedInView} selected</span>
            )}
            <button
              onClick={handleBulkDelete}
              disabled={selectedInView === 0 || bulkDeleting}
              className="flex items-center gap-2 px-3.5 py-2 rounded-lg border border-gray-200 text-sm font-medium text-[#64748B] bg-white hover:bg-red-50 hover:text-red-600 hover:border-red-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {bulkDeleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
              Delete Selected
            </button>
          </div>
        </div>

        {/* Table card */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          {/* Showing count */}
          <div className="px-5 py-3 border-b border-gray-50 flex items-center justify-between">
            <span className="text-xs text-[#94A3B8]">
              {isLoading ? 'Loading…' : `Showing ${filtered.length} module${filtered.length !== 1 ? 's' : ''}`}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <TableHeadRow>
                  <SortTh
                    sortable={false}
                    className="w-10"
                    label={
                      <input
                        type="checkbox"
                        checked={allChecked}
                        ref={el => { if (el) el.indeterminate = someChecked && !allChecked; }}
                        onChange={toggleAll}
                        className="h-4 w-4 rounded border-gray-300 accent-[#6366F1] cursor-pointer"
                      />
                    }
                  />
                  {['S.No', 'Icon', 'Module Name', 'Page Link', 'Type', 'Parent Module', 'Order', 'Status', 'Action'].map(h => (
                    <SortTh key={h} label={h} sortable={false} className="whitespace-nowrap" />
                  ))}
                </TableHeadRow>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {isLoading ? (
                  <tr>
                    <td colSpan={10} className="px-4 py-16 text-center">
                      <div className="flex justify-center">
                        <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#6366F1] border-t-transparent" />
                      </div>
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-4 py-16 text-center text-[#94A3B8] text-sm">
                      {search ? `No modules matching "${search}"` : 'No modules yet. Add your first module.'}
                    </td>
                  </tr>
                ) : filtered.map((mod, idx) => (
                  <tr
                    key={mod.id}
                    className={`hover:bg-gray-50/80 transition-colors ${selected.has(mod.id) ? 'bg-indigo-50/30' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selected.has(mod.id)}
                        onChange={() => toggleOne(mod.id)}
                        className="h-4 w-4 rounded border-gray-300 accent-[#6366F1] cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-3 text-[#94A3B8] text-xs">{idx + 1}</td>
                    <td className="px-4 py-3">
                      {mod.icon ? (
                        <div className="h-8 w-8 rounded-lg bg-indigo-50 flex items-center justify-center">
                          <i className={`${mod.icon} text-[#6366F1] text-sm`} />
                        </div>
                      ) : (
                        <span className="text-[#94A3B8]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => openEdit(mod)}
                        className={`font-medium hover:underline text-left transition-colors ${mod.type === 'M' ? 'text-[#6366F1] font-semibold' : 'text-[#6366F1]/80 pl-3'}`}
                      >
                        {mod.type === 'S' && (
                          <span className="text-[#94A3B8] mr-1.5">↳</span>
                        )}
                        {mod.name}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <code className="text-xs bg-gray-100 px-2 py-0.5 rounded text-[#64748B] font-mono">{mod.pageLink}</code>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                        mod.type === 'M'
                          ? 'border-[#6366F1] text-[#6366F1]'
                          : 'border-orange-400 text-orange-500'
                      }`}>
                        {mod.type === 'M' ? 'M' : 'S'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[#64748B] text-sm">
                      {mod.parent?.name
                        ? <span className="text-[#64748B]">{mod.parent.name}</span>
                        : <span className="text-[#CBD5E1]">—</span>}
                    </td>
                    <td className="px-4 py-3 text-[#64748B] text-center text-xs">{mod.orderNumber}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        mod.isActive
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-gray-100 text-gray-500'
                      }`}>
                        {mod.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {deletingIds.has(mod.id) ? (
                        <Loader2 className="h-4 w-4 animate-spin text-[#94A3B8]" />
                      ) : (
                        <ActionMenu
                          mod={mod}
                          onEdit={() => openEdit(mod)}
                          onDelete={() => handleDelete(mod)}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showDrawer && (
        <ModuleFormDrawer
          module={editModule}
          onClose={() => { setShowDrawer(false); setEditModule(null); }}
          onSaved={() => { setShowDrawer(false); setEditModule(null); fetchModules(); }}
          httpClient={saApi}
          defaultScope={activeScope}
        />
      )}
      <ConfirmModal
        isOpen={!!confirmMod}
        title="Delete Module?"
        message={`Delete module "${confirmMod?.name}"? This cannot be undone.`}
        confirmText="Delete"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmMod(null)}
        isLoading={confirmModLoading}
      />
      <ConfirmModal
        isOpen={confirmBulk}
        title="Delete Selected Modules?"
        message={`Delete ${[...selected].filter(id => filtered.some(m => m.id === id)).length} selected module(s)? This cannot be undone.`}
        confirmText="Delete"
        onConfirm={handleConfirmBulkDelete}
        onCancel={() => setConfirmBulk(false)}
        isLoading={confirmBulkLoading}
      />
    </SuperAdminLayout>
  );
}
